import type { TicketOrderListItem } from '@pulsify/shared/dto'
import { Copy, Mail } from 'lucide-react'
import type { ReactNode } from 'react'
import { formatMoney } from '~/entities/ticket/lib/ticket-earnings'
import * as m from '~/paraglide/messages'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '~/shared/ui/sheet'
import { countLabel, momentLabel } from '../lib/order-format'
import { copyEmail, type OrderActions } from './order-actions'
import {
  BuyerAvatar,
  BuyerName,
  EntryCount,
  OrderStatusChip,
  TicketStatusChip,
} from './order-parts'

export function OrderSheet({
  order,
  open,
  onOpenChange,
  actions,
  locale,
  now,
}: {
  order: TicketOrderListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
  actions: OrderActions
  locale: string
  now: Date
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        {order ? <OrderDetails order={order} actions={actions} locale={locale} now={now} /> : null}
      </SheetContent>
    </Sheet>
  )
}

function OrderDetails({
  order,
  actions,
  locale,
  now,
}: {
  order: TicketOrderListItem
  actions: OrderActions
  locale: string
  now: Date
}) {
  const money = (cents: number) => formatMoney(cents, order.currency, locale)
  const charged = order.status !== 'free'

  return (
    <>
      <div className="flex flex-col gap-4 border-border border-b px-5 pt-5 pb-4">
        <div className="flex min-w-0 items-center gap-3 pr-10">
          <BuyerAvatar name={order.buyerName} className="size-10" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <SheetTitle className="truncate">
              <BuyerName name={order.buyerName} className="font-semibold" />
            </SheetTitle>
            <SheetDescription className="truncate tabular-nums">
              {m.ticketPageSheetOrder({ reference: order.reference })}
            </SheetDescription>
          </div>
        </div>
        {order.buyerEmail ? (
          <div className="flex min-w-0 items-center gap-2 rounded-lg bg-muted py-1 pr-1 pl-3">
            <Mail aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <a
              href={`mailto:${order.buyerEmail}`}
              className="min-w-0 flex-1 truncate text-foreground text-sm underline-offset-4 hover:underline"
            >
              {order.buyerEmail}
            </a>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={m.ticketPageCopyEmail()}
              onClick={() => copyEmail(order.buyerEmail ?? '')}
            >
              <Copy />
            </Button>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <OrderStatusChip status={order.status} disputed={order.disputed} />
          <span className="text-muted-foreground text-sm">
            <EntryCount order={order} />
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        <dl className="divide-y divide-border text-sm">
          <Detail label={m.ticketPageDetailReference()}>
            <span className="tabular-nums">{order.reference}</span>
          </Detail>
          <Detail label={m.ticketPageColumnType()}>{order.ticketTypeName}</Detail>
          <Detail label={m.ticketPageColumnQuantity()}>{countLabel(order.quantity, locale)}</Detail>
          {charged ? (
            <>
              <Detail label={m.ticketPageDetailUnitPrice()}>{money(order.unitPriceCents)}</Detail>
              <Detail label={m.ticketPageDetailFee()}>{money(order.applicationFeeCents)}</Detail>
              <Detail label={m.ticketPageColumnAmount()}>
                <span className="font-medium">{money(order.totalCents)}</span>
              </Detail>
              {order.status === 'paid' ? (
                <Detail label={m.ticketPageDetailNet()}>
                  {money(order.totalCents - order.applicationFeeCents)}
                </Detail>
              ) : null}
            </>
          ) : null}
          <Detail label={m.ticketPageColumnOrderedAt()}>
            {momentLabel(order.createdAt, locale, now)}
          </Detail>
          {order.paidAt ? (
            <Detail label={m.ticketPageDetailPaidAt()}>
              {momentLabel(order.paidAt, locale, now)}
            </Detail>
          ) : null}
        </dl>

        <h3 className="mt-6 mb-2 font-medium text-foreground text-sm">{m.ticketsTitle()}</h3>
        <ul className="divide-y divide-border rounded-lg border border-border">
          {order.tickets.map((ticket) => (
            <li key={ticket.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground text-sm tabular-nums">
                  {ticket.reference}
                </p>
                <p className="truncate text-muted-foreground text-xs">
                  {ticket.checkedInAt
                    ? m.ticketPageCheckedInAt({
                        time: momentLabel(ticket.checkedInAt, locale, now),
                      })
                    : ticket.status === 'active'
                      ? m.ticketPageNotCheckedIn()
                      : ''}
                  {ticket.holderName && ticket.holderName !== order.buyerName
                    ? ` · ${m.ticketPageHolder({ name: ticket.holderName })}`
                    : ''}
                </p>
              </div>
              {ticket.isStudent ? (
                <Badge variant="secondary">{m.ticketsStudentBadge()}</Badge>
              ) : null}
              {ticket.status === 'active' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={actions.busy}
                  onClick={() => actions.askVoid(order, ticket)}
                >
                  {m.ticketsVoid()}
                </Button>
              ) : (
                <TicketStatusChip status={ticket.status} />
              )}
            </li>
          ))}
        </ul>
      </div>

      {order.status === 'paid' || order.status === 'refunded' ? (
        <div className="border-border border-t px-5 py-4">
          <Button variant="outline" className="w-full" onClick={() => actions.openInvoice(order)}>
            {m.ticketsInvoiceOpen()}
          </Button>
        </div>
      ) : null}

      {order.status === 'paid' && !order.disputed ? (
        <div className="border-border border-t px-5 py-4">
          <Button
            variant="outline"
            className="w-full text-destructive hover:text-destructive"
            disabled={actions.busy}
            onClick={() => actions.askRefund(order)}
          >
            {m.ticketPageRefundOrder()}
          </Button>
        </div>
      ) : null}
    </>
  )
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right text-foreground tabular-nums">{children}</dd>
    </div>
  )
}
